import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Kanban, Warning } from "@phosphor-icons/react";

const priorityColor = {
  low: "text-gray-500 border-gray-300",
  medium: "text-[#002FA7] border-[#002FA7]",
  high: "text-[#F59E0B] border-[#F59E0B]",
  urgent: "text-[#FF3B30] border-[#FF3B30]",
};

const typeIcon = { task: "▪", story: "◆", bug: "●", epic: "★" };

export default function AllTasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [filters, setFilters] = useState({
    project_id: "",
    assignee_id: "",
    status: "",
    priority: "",
    task_type: "",
    mine: false,
    q: "",
    due_before: "",
    due_after: "",
  });
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState("due_date");
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    api.get("/projects").then(r => setProjects(r.data || []));
    api.get("/users").then(r => setMembers(r.data || [])).catch(() => setMembers([]));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== "" && v !== false && v != null) params[k] = v;
      });
      const r = await api.get("/tasks", { params });
      setTasks(r.data.tasks || []);
    } catch {
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const applyFilters = () => load();

  const sortedTasks = useMemo(() => {
    const copy = [...tasks];
    copy.sort((a, b) => {
      const av = a[sortKey] || "";
      const bv = b[sortKey] || "";
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [tasks, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("asc"); }
  };

  const stats = useMemo(() => {
    const total = tasks.length;
    const done = tasks.filter(t => t.status === "done").length;
    const overdue = tasks.filter(t => t.is_overdue).length;
    const unassigned = tasks.filter(t => !t.assignee_id).length;
    return { total, done, overdue, unassigned };
  }, [tasks]);

  return (
    <div>
      <div className="border-b border-border px-8 lg:px-12 py-8">
        <div className="eyebrow mb-2">Work / Global Tasks</div>
        <h1 className="font-display text-4xl font-black tracking-tight">All Tasks</h1>
        <p className="text-sm text-muted-foreground mt-2">Every task across projects — filterable, sortable, actionable.</p>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-4 border-b border-border">
        <div className="grid-cell p-6" data-testid="stat-total">
          <div className="eyebrow mb-1">Total</div>
          <div className="font-display text-3xl font-black">{stats.total}</div>
        </div>
        <div className="grid-cell p-6" data-testid="stat-done">
          <div className="eyebrow mb-1">Done</div>
          <div className="font-display text-3xl font-black text-[#22C55E]">{stats.done}</div>
        </div>
        <div className="grid-cell p-6" data-testid="stat-overdue">
          <div className="eyebrow mb-1">Overdue</div>
          <div className="font-display text-3xl font-black text-[#FF3B30]">{stats.overdue}</div>
        </div>
        <div className="grid-cell p-6" data-testid="stat-unassigned">
          <div className="eyebrow mb-1">Unassigned</div>
          <div className="font-display text-3xl font-black text-muted-foreground">{stats.unassigned}</div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="border-b border-border px-8 lg:px-12 py-4 flex flex-wrap gap-2 items-center bg-muted/30">
        <select className="border border-border bg-white px-3 py-2 text-sm" value={filters.project_id}
          onChange={e => setFilters(f => ({ ...f, project_id: e.target.value }))} data-testid="filter-project">
          <option value="">All projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select className="border border-border bg-white px-3 py-2 text-sm" value={filters.assignee_id}
          onChange={e => setFilters(f => ({ ...f, assignee_id: e.target.value }))} data-testid="filter-assignee">
          <option value="">All assignees</option>
          {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <select className="border border-border bg-white px-3 py-2 text-sm" value={filters.status}
          onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} data-testid="filter-status">
          <option value="">Any status</option>
          <option value="todo">Todo</option>
          <option value="in_progress">In progress</option>
          <option value="review">Review</option>
          <option value="done">Done</option>
        </select>
        <select className="border border-border bg-white px-3 py-2 text-sm" value={filters.priority}
          onChange={e => setFilters(f => ({ ...f, priority: e.target.value }))} data-testid="filter-priority">
          <option value="">Any priority</option>
          <option value="low">Low</option><option value="medium">Medium</option>
          <option value="high">High</option><option value="urgent">Urgent</option>
        </select>
        <select className="border border-border bg-white px-3 py-2 text-sm" value={filters.task_type}
          onChange={e => setFilters(f => ({ ...f, task_type: e.target.value }))} data-testid="filter-type">
          <option value="">Any type</option>
          <option value="task">Task</option><option value="story">Story</option>
          <option value="bug">Bug</option><option value="epic">Epic</option>
        </select>
        <input type="text" placeholder="Search title/description…" className="border border-border bg-white px-3 py-2 text-sm min-w-[220px]"
          value={filters.q} onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
          onKeyDown={e => e.key === "Enter" && applyFilters()} data-testid="filter-search" />
        <label className="flex items-center gap-1.5 text-sm cursor-pointer">
          <input type="checkbox" checked={filters.mine} onChange={e => setFilters(f => ({ ...f, mine: e.target.checked }))} data-testid="filter-mine" />
          Only mine
        </label>
        <button className="btn-primary" onClick={applyFilters} disabled={loading} data-testid="apply-filters">
          {loading ? "Loading…" : "Apply"}
        </button>
      </div>

      {/* Task table */}
      <div className="p-8 lg:p-12">
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted">
                <th className="text-left px-4 py-3 eyebrow cursor-pointer" onClick={() => toggleSort("title")}>Task</th>
                <th className="text-left px-4 py-3 eyebrow cursor-pointer" onClick={() => toggleSort("project_name")}>Project</th>
                <th className="text-left px-4 py-3 eyebrow">Assignee</th>
                <th className="text-left px-4 py-3 eyebrow cursor-pointer" onClick={() => toggleSort("status")}>Status</th>
                <th className="text-left px-4 py-3 eyebrow">Priority</th>
                <th className="text-right px-4 py-3 eyebrow">Points</th>
                <th className="text-left px-4 py-3 eyebrow cursor-pointer" onClick={() => toggleSort("due_date")}>Due</th>
              </tr>
            </thead>
            <tbody>
              {sortedTasks.map(t => (
                <tr key={t.id} className="border-b border-border hover:bg-muted/50" data-testid={`task-row-${t.id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/projects/${t.project_id}?task=${t.id}`} className="font-medium hover:text-[#002FA7]">
                      <span className="mr-1.5 text-muted-foreground">{typeIcon[t.task_type] || "▪"}</span>
                      {t.title}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    <Link to={`/projects/${t.project_id}`} className="hover:text-[#002FA7]">{t.project_name || "—"}</Link>
                  </td>
                  <td className="px-4 py-3 text-xs">{t.assignee_name || <span className="text-muted-foreground italic">Unassigned</span>}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 border border-border font-mono-mini uppercase">{t.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 border font-mono-mini uppercase ${priorityColor[t.priority] || ""}`}>
                      {t.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono-mini text-xs">{t.points || 0}</td>
                  <td className="px-4 py-3 font-mono-mini text-xs">
                    {t.due_date ? (
                      <span className={t.is_overdue ? "text-[#FF3B30] font-semibold flex items-center gap-1" : ""}>
                        {t.is_overdue && <Warning size={12} weight="bold" />}
                        {t.due_date}
                      </span>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                </tr>
              ))}
              {sortedTasks.length === 0 && (
                <tr><td colSpan={7} className="p-12 text-center text-muted-foreground">
                  <Kanban size={32} weight="bold" className="mx-auto mb-2 opacity-40" />
                  {loading ? "Loading…" : "No tasks match these filters."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
